import type { ExcelEditorHandle, SheetCell } from "@/components/ExcelEditor"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { TextAreaStateAction } from "@/types/hooks/useTextAreaStates"
import type { QuestionnaireResponsePair } from "@/types/questionnaire/models"
import { normalizeSheetName } from "@/utils/urls.const"
import { cellAddressToIndices } from "@/utils/viewerUtils"
import { WorkbookInstance } from "@fortune-sheet/react"
import { MutableRefObject, RefObject, useRef } from "react"

import { SHEET_ACTIVATION_DELAY_MS } from "./constants"

interface ActiveCell {
  row: number | undefined
  column: number | undefined
  /** The sheet, tracked for the new editor only. */
  sheet?: string
}

type UpdateTextAreaStateFn = (action: TextAreaStateAction) => void

/** Where an answer goes in the workbook. */
type AnswerCells = Pick<
  QuestionnaireResponsePair,
  "sheetName" | "answerTargetsJson"
>

interface UseExcelCellSyncOptions {
  workbookRef: MutableRefObject<WorkbookInstance | null>
  /** The new editor's handle, used instead while EXCEL_EDITOR_V2 is on. */
  editorRef?: RefObject<ExcelEditorHandle | null>
  updateTextAreaState: UpdateTextAreaStateFn
}

export type UpdateExcelCellFunctionType = (
  sheetName: string,
  cellAddress: string,
  text: string,
  skipUpdate?: boolean,
) => void

/** The workbook's spelling of an API sheet name; the shown sheet without one. */
const editorSheet = (
  editor: ExcelEditorHandle,
  sheetName: string | null | undefined,
) => {
  if (!sheetName) return editor.getActiveSheet()?.name
  // The API's sheet names are compared the way the old viewer compared them.
  const wanted = normalizeSheetName(sheetName)
  return editor
    .getSheets()
    .find((candidate) => normalizeSheetName(candidate.name) === wanted)?.name
}

/** Run `apply` now, or once the workbook has loaded. */
const onceLoaded = (editor: ExcelEditorHandle, apply: () => void) => {
  if (editor.getSheets().length > 0) apply()
  else
    void editor.whenReady().then(
      () => {
        try {
          apply()
        } catch {
          // The preview failing never blocks the API update.
        }
      },
      () => {},
    )
}

export const closeFortuneSheetInlineEditor = () => {
  try {
    const editor = document.getElementById(
      "luckysheet-rich-text-editor",
    ) as HTMLElement | null

    // Focus/dispatch target: the editable child if present, else the editor, else workbook root, else body.
    const cellInput =
      (editor?.querySelector(".luckysheet-cell-input") as HTMLElement | null) ??
      (document.querySelector(".luckysheet-cell-input") as HTMLElement | null)

    const root = document.querySelector(
      "[data-excel-viewer-root]",
    ) as HTMLElement | null

    const activeElement = document.activeElement as HTMLElement | null
    const isFocusInsideExcel = !!(
      activeElement &&
      ((cellInput && cellInput.contains(activeElement)) ||
        (editor && editor.contains(activeElement)) ||
        (root && root.contains(activeElement)))
    )

    // Preserve focus in the Q&A panel. Only close/blur when Excel currently owns focus.
    if (!isFocusInsideExcel) {
      return
    }

    const target = cellInput ?? editor ?? root
    if (!target) {
      return
    }

    // ESC keydown usually exits Luckysheet/Fortune Sheet edit mode.
    const escDown = new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      bubbles: true,
      cancelable: true,
    })
    target.dispatchEvent(escDown)

    // Some implementations listen on keyup too.
    const escUp = new KeyboardEvent("keyup", {
      key: "Escape",
      code: "Escape",
      bubbles: true,
      cancelable: true,
    })
    target.dispatchEvent(escUp)

    // Blur whatever is focused as a fallback.
    activeElement?.blur?.()
    cellInput?.blur?.()
  } catch {
    // best-effort only
  }
}

/**
 * Custom hook that synchronizes text area state changes with Excel cell updates.
 * Handles sheet activation, cell value updates, selection, and scrolling.
 */
export const useExcelCellSync = ({
  workbookRef,
  editorRef,
  updateTextAreaState,
}: UseExcelCellSyncOptions) => {
  const newEditor = useFeatureFlag("EXCEL_EDITOR_V2")
  const currentActiveCell = useRef<ActiveCell>({
    row: undefined,
    column: undefined,
  })

  /**
   * The new editor writes and lands synchronously, so none of the old
   * viewer's deferrals are needed; a write before the workbook loaded waits
   * for it.
   */
  const updateEditorCell = (
    editor: ExcelEditorHandle,
    sheetName: string,
    cellAddress: string,
    text: string,
    skipUpdate: boolean,
  ) => {
    // Parsed now, so a bad address fails inside the caller's try rather than
    // in the deferred callback below.
    const { row, column } = cellAddressToIndices(cellAddress)
    onceLoaded(editor, () => {
      const sheet = editorSheet(editor, sheetName)
      if (!sheet) return
      if (!skipUpdate)
        // As if typed, the way Fortune Sheet's setCellValue read it: a number
        // answer shows through the cell's own number format.
        editor.setCellValues([{ sheet, row, column, value: text, typed: true }])
      const current = currentActiveCell.current
      if (
        row === current.row &&
        column === current.column &&
        sheet === current.sheet
      )
        return
      currentActiveCell.current = { row, column, sheet }
      editor.jump({
        sheet,
        range: {
          startRow: row,
          endRow: row,
          startColumn: column,
          endColumn: column,
        },
      })
    })
  }

  /**
   * Wrap the cells of every answer, so answers the file already holds show
   * whole (their rows grow with `autoRowHeight`) as written ones do. For the
   * new editor's load; the old viewer shows the file as it is.
   */
  const wrapAnswerCells = (pairs: readonly AnswerCells[]) => {
    const editor = editorRef?.current
    if (!newEditor || !editor) return
    try {
      onceLoaded(editor, () => {
        const cells: SheetCell[] = []
        for (const { sheetName, answerTargetsJson } of pairs) {
          const sheet = editorSheet(editor, sheetName)
          if (!sheet) continue
          for (const { addr } of answerTargetsJson ?? []) {
            try {
              cells.push({ sheet, ...cellAddressToIndices(addr) })
            } catch {
              // Not a cell address: nothing to wrap.
            }
          }
        }
        if (cells.length > 0) editor.formatCells(cells, { wrap: true })
      })
    } catch {
      // Same contract as updateExcelCell: the preview failing blocks nothing.
    }
  }

  const updateExcelCell = (
    sheetName: string,
    cellAddress: string,
    text: string,
    skipUpdate: boolean = false,
  ) => {
    if (newEditor) {
      const editor = editorRef?.current
      if (!cellAddress || !editor) return
      try {
        updateEditorCell(editor, sheetName, cellAddress, text, skipUpdate)
      } catch {
        // Same contract as below: the preview failing never blocks the API update.
      }
      return
    }
    try {
      if (cellAddress && workbookRef.current) {
        const { row, column } = cellAddressToIndices(cellAddress)

        // Use setTimeout to defer Fortune Sheet operations this prevents infinite update loops when switching sheets
        setTimeout(() => {
          if (!workbookRef.current) return

          if (sheetName) {
            const allSheets = workbookRef.current.getAllSheets()
            const normalizedTargetSheetName = normalizeSheetName(sheetName)
            const targetSheet = allSheets?.find(
              (sheet: { name: string }) =>
                normalizeSheetName(sheet.name) === normalizedTargetSheetName,
            )
            if (targetSheet?.id) {
              workbookRef.current.activateSheet({ id: targetSheet.id })
            }
          }

          // Update cell value, select, and scroll to it after a small delay
          // to allow sheet activation to complete
          setTimeout(() => {
            if (!workbookRef.current) return

            if (!skipUpdate) {
              // Close any open in-cell editor to avoid stale editor state overriding our external update.
              closeFortuneSheetInlineEditor()
            }

            if (!skipUpdate) {
              workbookRef.current.setCellValue(row, column, text)
            }

            // Only scroll/select if we're moving to a different cell
            // This prevents infinite loops from Fortune Sheet's internal state updates
            if (
              row !== currentActiveCell.current.row ||
              column !== currentActiveCell.current.column
            ) {
              // Update the ref BEFORE calling Fortune Sheet methods
              // to prevent re-entry if these methods trigger re-renders
              currentActiveCell.current.row = row
              currentActiveCell.current.column = column

              // Use requestAnimationFrame to batch DOM updates
              requestAnimationFrame(() => {
                if (!workbookRef.current) return
                workbookRef.current.setSelection([
                  { row: [row, row], column: [column, column] },
                ])
                const scrollRowOffset = 5
                const upperRow =
                  row - scrollRowOffset >= 0 ? row - scrollRowOffset : 0

                const scrollColumnOffset = 1
                const previousColumn =
                  column - scrollColumnOffset >= 0
                    ? column - scrollColumnOffset
                    : column

                workbookRef.current.scroll({
                  targetRow: upperRow,
                  targetColumn: previousColumn,
                })
              })
            }
          }, SHEET_ACTIVATION_DELAY_MS)
        }, 0)
      }
    } catch {
      // Skip local excel modification failure and call so that update API always gets called
    }
  }

  const updateDataToApi = (action: TextAreaStateAction) => {
    updateTextAreaState(action)
  }

  return {
    currentActiveCell,
    updateExcelCell,
    updateDataToApi,
    wrapAnswerCells,
  }
}
